import * as Sentry from "@sentry/react";
import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";

// Shared onSuccess handler - identical across all repos
export const onSuccess = (response: AxiosResponse) => {
  Sentry.endSession();
  return response;
};

// Shared Sentry request error handler
const onRequestError = (error: AxiosError) => {
  if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
    Sentry.captureException(error);
  }
  Sentry.endSession();
  return Promise.reject(error);
};

export type GlobalApiConfig = {
  apiHost: string;
  onError: (error: AxiosError) => Promise<unknown>;
  getAuthToken?: () => string | undefined;
};

// Module-level singleton - live ES module binding so consumers see the initialized instance
export let globalApi: AxiosInstance;

/**
 * Creates and initializes the globalApi axios instance.
 * Must be called once by the host app before rendering.
 *
 * @example
 * // In host app's entry point (e.g. src/index.tsx)
 * import { createGlobalApi } from "@/modules/irohana-components/utils/api";
 * import { onError } from "@/utils/api";
 *
 * createGlobalApi({
 *   apiHost: process.env.REACT_APP_API_HOST!,
 *   onError,
 * });
 */
export const createGlobalApi = (config: GlobalApiConfig) => {
  globalApi = axios.create({ baseURL: config.apiHost });

  globalApi.interceptors.request.use((reqConfig) => {
    const token = config.getAuthToken?.();
    if (token) {
      reqConfig.headers.Authorization = `Bearer ${token}`;
    }
    Sentry.startSession();
    return reqConfig;
  }, onRequestError);

  globalApi.interceptors.response.use(onSuccess, config.onError);

  return globalApi;
};
